import React, { useState, useRef } from 'react'
import { HiPlus, HiX } from 'react-icons/hi'

function OptionsTagInput({
    options = [],
    onChange,
    disabled = false,
    maxTags = 20,
    maxLength = 50,
    placeholder = 'Add option...'
}) {
    const [inputValue, setInputValue] = useState('')
    const [error, setError] = useState('')
    const inputRef = useRef(null)

    const handleInputChange = (e) => {
        const value = e.target.value
        if (value.length <= maxLength) {
            setInputValue(value)
            setError('')
        }
    }

    const handleAddOption = () => {
        const trimmedValue = inputValue.trim()

        if (!trimmedValue) {
            return
        }

        if (options.length >= maxTags) {
            setError(`Maximum ${maxTags} options allowed`)
            return
        }

        if (options.some(opt => opt.toLowerCase() === trimmedValue.toLowerCase())) {
            setError('Duplicate option')
            return
        }

        onChange([...options, trimmedValue])
        setInputValue('')
        setError('')
        inputRef.current?.focus()
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleAddOption()
        } else if (e.key === 'Backspace' && !inputValue && options.length > 0) {
            // Remove last tag on backspace when input is empty
            onChange(options.slice(0, -1))
        }
    }

    const handleRemoveOption = (index) => {
        const newOptions = options.filter((_, i) => i !== index)
        onChange(newOptions)
    }

    return (
        <div style={{ width: '100%' }}>
            <div
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.5rem',
                    padding: '0.5rem',
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-md)',
                    minHeight: '42px',
                    alignItems: 'center',
                    transition: 'all 0.2s ease',
                    opacity: disabled ? 0.5 : 1
                }}
                onClick={() => inputRef.current?.focus()}
            >
                {/* Existing tags */}
                {options.map((option, index) => (
                    <span
                        key={index}
                        className="option-tag"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            padding: '0.25rem 0.5rem',
                            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            borderRadius: 'var(--radius-full)',
                            fontSize: '0.8rem',
                            fontWeight: '500',
                            color: 'var(--text-primary)',
                            animation: 'fadeIn 0.2s ease-out'
                        }}
                    >
                        <span style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {option}
                        </span>
                        {!disabled && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    handleRemoveOption(index)
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '16px',
                                    height: '16px',
                                    padding: 0,
                                    background: 'rgba(239, 68, 68, 0.2)',
                                    border: 'none',
                                    borderRadius: '50%',
                                    color: 'var(--error-color)',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'var(--error-color)'
                                    e.currentTarget.style.color = 'white'
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'
                                    e.currentTarget.style.color = 'var(--error-color)'
                                }}
                            >
                                <HiX style={{ fontSize: '0.7rem' }} />
                            </button>
                        )}
                    </span>
                ))}

                {/* Input field */}
                {!disabled && options.length < maxTags && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flex: '1 1 100px', minWidth: '80px' }}>
                        <input
                            ref={inputRef}
                            type="text"
                            value={inputValue}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            placeholder={options.length === 0 ? placeholder : ''}
                            disabled={disabled}
                            style={{
                                flex: 1,
                                minWidth: '60px',
                                padding: '0.25rem',
                                background: 'transparent',
                                border: 'none',
                                outline: 'none',
                                color: 'var(--text-primary)',
                                fontSize: '0.85rem'
                            }}
                        />
                        {inputValue && (
                            <button
                                type="button"
                                onClick={handleAddOption}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '22px',
                                    height: '22px',
                                    padding: 0,
                                    background: 'var(--accent-primary)',
                                    border: 'none',
                                    borderRadius: '50%',
                                    color: 'white',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                    flexShrink: 0
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'scale(1.1)'
                                    e.currentTarget.style.boxShadow = 'var(--glow-primary)'
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'scale(1)'
                                    e.currentTarget.style.boxShadow = 'none'
                                }}
                            >
                                <HiPlus style={{ fontSize: '0.8rem' }} />
                            </button>
                        )}
                    </div>
                )}

                {/* Max reached indicator */}
                {options.length >= maxTags && (
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                        Max {maxTags} options
                    </span>
                )}
            </div>

            {/* Error message */}
            {error && (
                <div style={{
                    marginTop: '0.25rem',
                    fontSize: '0.75rem',
                    color: 'var(--error-color)',
                    animation: 'fadeIn 0.2s ease-out'
                }}>
                    {error}
                </div>
            )}

            {/* Character count */}
            {inputValue && (
                <div style={{
                    marginTop: '0.25rem',
                    fontSize: '0.7rem',
                    color: 'var(--text-muted)',
                    textAlign: 'right'
                }}>
                    {inputValue.length}/{maxLength}
                </div>
            )}
        </div>
    )
}

export default OptionsTagInput
